import { ApiError, parseApiError } from './errors'

/**
 * Tiny fetch wrapper around the backend `/api` surface.
 *
 * Why a wrapper:
 *   - Always include credentials (session cookie is httpOnly + SameSite=Lax).
 *   - Centralise error parsing into ApiError so call-sites can switch on
 *     `code` instead of parsing JSON.
 *   - Make 401 a special-case error that the router catches globally,
 *     not something every screen has to remember.
 *   - Keep this thin: domain logic (auth, loops, matches…) lives in its
 *     own module and uses this as a transport.
 *
 * What it does NOT do:
 *   - File uploads (use axios for upload progress; see api/uploads.ts).
 *   - Runtime schema validation (callers decide; AI endpoints wrap in Zod).
 *   - Retries (TanStack Query handles retry policy at the query level).
 */

type Json = unknown

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON body — will be stringified and Content-Type set automatically. */
  json?: Json
  /** Raw body (FormData, Blob, …) — sent as-is. */
  body?: BodyInit | null
  /** AbortSignal for cancellation. */
  signal?: AbortSignal
}

const BASE = '/api'

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { json, headers, body, ...rest } = opts

  const finalHeaders = new Headers(headers)
  let finalBody: BodyInit | null | undefined = body

  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json')
    finalBody = JSON.stringify(json)
  }
  // Ask the backend to skip HTML responses on errors (defensive).
  if (!finalHeaders.has('Accept')) {
    finalHeaders.set('Accept', 'application/json')
  }

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...rest,
      headers: finalHeaders,
      body: finalBody,
    })
  } catch (err) {
    // Network failure (DNS, offline, CORS-rejected, aborted).
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError({
      code: 'network_error',
      message: 'Network request failed',
      status: 0,
    })
  }

  if (res.status === 204) {
    return undefined as T
  }
  if (!res.ok) {
    throw await parseApiError(res)
  }

  // 2xx with body — assume JSON.
  try {
    return (await res.json()) as T
  } catch {
    throw new ApiError({
      code: 'parse_error',
      message: 'Server returned malformed JSON',
      status: res.status,
    })
  }
}

/* -------------------------------------------------------------------------
 * Convenience verbs — pure sugar over `request`.
 * ------------------------------------------------------------------------- */

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'json' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),

  post: <T>(path: string, json?: Json, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'POST', json }),

  put: <T>(path: string, json?: Json, opts?: Omit<RequestOptions, 'method' | 'json'>) =>
    request<T>(path, { ...opts, method: 'PUT', json }),

  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'json' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}
